import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, Volume2, Square, MessageSquare } from "lucide-react";
import { useApp } from "../context/AppContext";
import { sendChat } from "../lib/api";
import { Markdown } from "../lib/markdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

export default function Chat() {
  const { sessions, voiceURI } = useApp();
  const [scope, setScope] = useState("library");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const endRef = useRef(null);
  const sessionId = useRef(crypto.randomUUID());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => () => window.speechSynthesis?.cancel(), []);

  const buildContext = () => {
    let list = sessions;
    if (scope !== "library") list = sessions.filter((s) => s.id === scope);
    return list.slice(0, 10).map((s, i) =>
      `SESSION ${i + 1}\nQ: ${s.question}\nAnswer: ${(s.synthesis || "").slice(0, 800)}`).join("\n\n");
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const history = [...messages, userMsg];
    setMessages(history); setInput(""); setLoading(true);
    try {
      const { reply } = await sendChat({
        session_id: sessionId.current, message: userMsg.content,
        context: buildContext(), history: messages,
      });
      setMessages([...history, { role: "assistant", content: reply }]);
    } catch (e) {
      toast.error("Chat failed. Please try again.");
      setMessages(history);
    } finally { setLoading(false); }
  };

  const speak = (text, idx) => {
    if (!window.speechSynthesis) { toast.error("Speech not supported in this browser."); return; }
    if (speakingIdx === idx) { window.speechSynthesis.cancel(); setSpeakingIdx(null); return; }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/[#*`>]/g, ""));
    const voices = window.speechSynthesis.getVoices();
    const v = voices.find((x) => x.voiceURI === voiceURI);
    if (v) u.voice = v;
    u.onend = () => setSpeakingIdx(null);
    setSpeakingIdx(idx); window.speechSynthesis.speak(u);
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 flex flex-col" style={{ minHeight: "calc(100vh - 4rem)" }}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ color: "#1E293B" }}>Conversation</h1>
          <p className="mt-1" style={{ color: "#475569" }}>Ask follow-up questions about your saved knowledge.</p>
        </div>
        <div className="w-56">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger data-testid="chat-scope-select" className="rounded-xl" style={{ borderColor: "#CBD5E1" }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="library">Entire Library</SelectItem>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.question.slice(0, 40)}…</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 mt-6 space-y-4">
        {messages.length === 0 && !loading && (
          <div className="text-center py-16">
            <div className="grid place-items-center w-14 h-14 rounded-2xl mx-auto mb-4" style={{ background: "#EFF6FF" }}>
              <MessageSquare size={24} color="#2563EB" />
            </div>
            <p style={{ color: "#475569" }}>
              {sessions.length === 0 ? "Save some sessions first, then chat about them here." : "Start a conversation about your library."}
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl px-4 py-3 text-[15px]" style={{ background: "#F1F5F9", color: "#1E293B" }}>{m.content}</div>
            </div>
          ) : (
            <div key={i} className="flex justify-start">
              <div className="max-w-[90%] rounded-2xl border bg-white px-5 py-4 shadow-sm" style={{ borderColor: "#CBD5E1" }} data-testid="ai-message">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: "#2563EB" }}>Acuman</span>
                  <button onClick={() => speak(m.content, i)} data-testid="listen-btn"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg transition-colors duration-200"
                    style={{ background: speakingIdx === i ? "#ECFDF5" : "#F8FAFC", color: speakingIdx === i ? "#047857" : "#475569" }}>
                    {speakingIdx === i ? <><Square size={13} className="pulse-soft" /> Stop</> : <><Volume2 size={13} /> Listen</>}
                  </button>
                </div>
                <Markdown text={m.content} />
              </div>
            </div>
          )
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="rounded-2xl border bg-white px-5 py-4 shadow-sm flex items-center gap-2" style={{ borderColor: "#CBD5E1", color: "#94A3B8" }}>
              <Loader2 size={16} className="animate-spin" /> Thinking…
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-4 mt-4 flex items-center gap-2 bg-white border rounded-2xl p-2 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} data-testid="chat-input"
          placeholder="Ask a follow-up…" className="flex-1 px-3 h-10 outline-none text-[15px]" style={{ color: "#1E293B" }} />
        <button onClick={send} disabled={loading || !input.trim()} data-testid="chat-send-btn"
          className="h-10 px-4 rounded-xl text-white grid place-items-center transition-colors duration-200 disabled:opacity-50" style={{ background: "#2563EB" }}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
